/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { useAuthHttp } from '@hooks';
import { FLOX_LOGO_BLUE_SRC, FORGOTTEN_PASSWORD_URL } from '@constants';
import {
  ExclamationTriangleIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../tailwindComponents/Button';

interface ForgotPasswordValues {
  email: string;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address.')
    .required('An email address is required.'),
});

export function ForgottenPassword() {
  const navigate = useNavigate();
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryEmailSuccess, setRecoveryEmailSuccess] = useState(false);
  const [recoveryEmailErrorText, setRecoveryEmailErrorText] = useState('');
  const [recoveryEmailSendError, setRecoveryEmailSendError] = useState(false);
  const authHttp = useAuthHttp();

  useDocumentTitle('FLOX - Forgotten Password');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ForgotPasswordValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values: ForgotPasswordValues): Promise<void> => {
    await authHttp
      .post(FORGOTTEN_PASSWORD_URL, {
        email: values.email,
      })
      .then(() => {
        reset();
        setRecoveryEmail(values.email);
        setRecoveryEmailSuccess(true);
      })
      .catch(() => {
        reset();
        setRecoveryEmailErrorText('Something went wrong.');
        setRecoveryEmailSendError(true);
      });
  };

  return (
    <main className='flex min-h-screen w-full'>
      {/* Image Panel */}
      <section
        className='hidden md:block md:w-1/2 min-h-screen bg-cover bg-no-repeat bg-center transition-all duration-300'
        style={{ backgroundImage: 'url(/images/bg-login.png)' }}
      />

      {/* Form Panel */}
      <section className='flex-1 flex items-center justify-center bg-white px-4 py-12'>
        <div className='w-full max-w-md'>
          {/* Logo */}
          <div className='flex justify-center mb-8'>
            <img
              src={FLOX_LOGO_BLUE_SRC}
              alt='Flox logo'
              className='h-14 w-auto'
            />
          </div>

          {/* Title */}
          <div className='text-center mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              {!recoveryEmailSuccess ? 'Forgot password?' : 'Check your email'}
            </h1>
            <p className='text-gray-500'>
              {!recoveryEmailSuccess
                ? "Enter your email and we'll send you a recovery link"
                : 'We sent a password reset link to your email'}
            </p>
          </div>

          {/* Card */}
          <div className='bg-white rounded-2xl shadow-lg border border-gray-100 p-8'>
            {!recoveryEmailSuccess ? (
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Email Field */}
                <div className='mb-6'>
                  <label
                    htmlFor='email'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    Email address
                  </label>
                  <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                      <div className='relative'>
                        <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                          <EnvelopeIcon className='h-5 w-5 text-gray-400' />
                        </div>
                        <input
                          {...field}
                          type='email'
                          id='email'
                          placeholder='you@company.com'
                          autoComplete='email'
                          disabled={isSubmitting}
                          className={`block w-full rounded-lg border py-3 pl-10 pr-3 text-sm transition-colors ${
                            errors.email
                              ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                              : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
                          } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                          onFocus={() => {
                            if (recoveryEmailSendError) {
                              setRecoveryEmailErrorText('');
                              setRecoveryEmailSendError(false);
                            }
                          }}
                        />
                      </div>
                    )}
                  />
                  {errors.email && (
                    <p className='mt-1.5 text-sm text-red-600'>
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type='submit'
                  intent='primary'
                  size='lg'
                  disabled={!isValid || isSubmitting}
                  className='w-full'
                  label={
                    isSubmitting
                      ? 'Sending recovery link...'
                      : 'Send recovery link'
                  }
                />

                {/* Error Message - shown below button for visibility */}
                {recoveryEmailSendError && (
                  <div className='rounded-lg bg-yellow-50 p-4 mt-4'>
                    <div className='flex'>
                      <ExclamationTriangleIcon className='h-5 w-5 text-yellow-400 flex-shrink-0' />
                      <p className='ml-3 text-sm text-yellow-700'>
                        {recoveryEmailErrorText}
                      </p>
                    </div>
                  </div>
                )}

                {/* Secured By */}
                <div className='flex items-center justify-center gap-2 mt-6 text-gray-400'>
                  <ShieldCheckIcon className='h-4 w-4' />
                  <span className='text-xs'>
                    Secured by FLOX Enterprise Authentication
                  </span>
                </div>
              </form>
            ) : (
              /* Success State */
              <div className='text-center'>
                <div className='flex justify-center mb-4'>
                  <div className='rounded-full bg-green-100 p-3'>
                    <CheckCircleIcon className='h-8 w-8 text-green-600' />
                  </div>
                </div>
                <p className='text-gray-600'>
                  A recovery link email was successfully sent to{' '}
                  <strong className='text-gray-900'>{recoveryEmail}</strong>
                </p>
                <p className='text-sm text-gray-500 mt-4'>
                  Didn&apos;t receive the email? Check your spam folder or{' '}
                  <button
                    type='button'
                    onClick={() => {
                      setRecoveryEmailSuccess(false);
                      setRecoveryEmail('');
                    }}
                    className='text-blue-600 hover:text-blue-800 hover:underline'
                  >
                    try again
                  </button>
                </p>
              </div>
            )}
          </div>

          {/* Footer Links */}
          <div className='mt-8 text-center'>
            <button
              type='button'
              onClick={() => navigate('/login')}
              className='inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline'
            >
              <ArrowLeftIcon className='h-4 w-4' />
              Back to login
            </button>

            <p className='text-sm text-gray-400 pt-6'>
              Need help? Contact{' '}
              <a
                href='mailto:support@flox.is'
                className='text-blue-600 hover:underline'
              >
                support@flox.is
              </a>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
